<template>
  <div class="tenant-projects">
    <TMSHeader
      :need-goback="false"
      :buttons="activeBtns"
      :title="tenancyInfoTitle"
      :status="status"
      :dotOption="dotOption"
      :lifecycleModal="lifecycleModal"
      :needOpenLifecycle="true"
    >
      <template slot="back-button">
        <div @mousedown.capture="suppressSubmissionAutosave">
          <HKLHeader
            link-class-left-primary="link-back-to-tenancy"
            :label-left-primary="tenantProjectHeaderLeftPrimaryLabel"
            :label-left-secondary="tenantProjectHeaderLeftSecondaryLabel"
            :label-right-leading="$t('listNavigation.previousRecord')"
            :label-right-trailing="$t('listNavigation.nextRecord')"
            :navigation-entries="tenantProjectHeaderNavigationEntries"
            :nav-base-path-hotb="hotbNavBasePath"
            :nav-base-path-tenant-project="tenantProjectNavBasePath"
            :current-record-id="tenantProjectHeaderCurrentRecordId"
            :navigate-prev-enabled="isUnderBatch ? canGoToPreviousWorkPermitRecord : undefined"
            :navigate-next-enabled="isUnderBatch ? canGoToNextWorkPermitRecord : undefined"
            @left-primary="onTenantProjectHklLeftPrimary"
            @left-secondary="navToAllTenantProjects"
            @right-leading="onTenantProjectHklPrevious"
            @right-trailing="onTenantProjectHklNext"
          />
        </div>
      </template>
    </TMSHeader>
    <Lifecycle
      ref="tenantProjectLifecycle"
      modalId="tenant-project-modules-lifecycle-modal"
      :type="'tenantProject'"
      :histories="histories"
      :commentAttr="lifecycleCommentAttrs.comments"
      :emptyHistoryHint="emptyHistoryHint"
    />

    <div class="tenant-projects-tabContainer">
      <TabsContainer
        ref="tab-container"
        :tabNames="tabNames"
        :getCurrentTab="getActiveIndex"
        :manualTabSelect="true"
        @tabMousedown="suppressSubmissionAutosave"
        @tabClick="onTabClick"
      >
        <template
          v-if="enableWorkPermitsFeature && isUnderBatch"
          slot="float-right"
        >
          <i
            class="batch-review-nav__link show-only-while-sticky-header-on"
            :class="{ 'batch-review-nav__link--disabled': !canGoToPreviousWorkPermitRecord }"
            @click="toPreviousWorkPermit"
          >
            &lt; {{ $t('listNavigation.previousRecord') }}
          </i>
          <i
            class="batch-review-nav__link show-only-while-sticky-header-on"
            :class="{ 'batch-review-nav__link--disabled': !canGoToNextWorkPermitRecord }"
            @click="toNextWorkPermit"
          >
              {{ $t('listNavigation.nextRecord') }} &gt;
            </i>
        </template>
        <template
          v-if="showSubmissionsCommentsPin"
          slot="label-1"
        >
          <span
            class="tenant-projects__submissions-tab-label"
            :class="{ 'tenant-projects__submissions-tab-label--single-line': !submissionsTabLabelParts.second }"
          >
            <span class="tenant-projects__submissions-tab-line tenant-projects__submissions-tab-line--primary">
              {{ submissionsTabLabelParts.first }}
            </span>
            <span class="tenant-projects__submissions-tab-pin-cell">
              <span class="pin__bubble-orange tenant-projects__tab-pin">
                <i
                  class="fa fa-thumb-tack"
                  aria-hidden="true"
                />
              </span>
            </span>
            <span
              v-if="submissionsTabLabelParts.second"
              class="tenant-projects__submissions-tab-line tenant-projects__submissions-tab-line--secondary"
            >
              {{ submissionsTabLabelParts.second }}
            </span>
          </span>
        </template>
        <ProjectInformation
          ref="projectInformation"
          slot="tab-0"
          :setHeaderBtn="setHeaderBtn"
          :canEdit="canEdit"
        />
        <SubmissionsEntry slot="tab-1" />
        <ContractorInfo
          v-if="enableWorkPermitsFeature"
          ref="contractorInfo"
          slot="tab-2"
        />
        <WorkPermitView
          v-if="enableWorkPermitsFeature"
          ref="work-permit-view"
          slot="tab-3"
        />
        <ProjectControl
          ref="ProjectControl"
          :slot="`${'tab-' + (tabNames.length - 1)}`"
        />
      </TabsContainer>
    </div>
    <DialogSlot
      id="tenant-project-status-dialog"
      ref="proj-ops-modal"
      :beforeCloseModal="beforeCloseModal"
    >
      <div
        slot="content"
        class="font-bold"
      >
        <span :class="{ 'required-flag': addComment }">{{ projectActionHint }}</span>
        <TextField
          v-if="addComment"
          class="withDraw"
          required
          :value="withdrawComment"
          :placeholder="$t('hint.withDrawReason')"
          @updateValue="val => withdrawComment = val"
        />
      </div>
      <div slot="buttons">
        <button
          v-hkldreset
          type="button"
          class="btn btn--orange close-reveal-modal"
          @click="exeOperate"
        >
          {{ addComment ? $t('action.confirmWithdraw') : $t('action.yesContinue') }}
        </button>
      </div>
    </DialogSlot>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { computed, getCurrentInstance, unref } from 'vue-demi'
import { LIST_NAV_KIND, mergeListNavigationHeaderPaths, pushListNavigationEntry, resolveNavigationKind } from '@/constants/listNavigation'
import { VIEW_ALL_RESUME_SEARCH_QUERY } from '@/constants/viewAllTableCache'
import { tenancyRecordsRouteNames } from '@/constants/router'
import TabsContainer from '@/controls/TabsContainer'
import mutationTypes from '@/store/mutation-types'
import DialogSlot from '@/controls/DialogSlot'
import TextField from '@/controls/TextField'
import HKLHeader from '@/controls/HKLHeader'
import TMSHeader from '@/controls/TMSHeader'
import navigation from '@/utils/navigation'
import { navigateToTenancyFromTenantProject } from '@/utils/tenancyNavigation'
import WorkPermitView from '@/modules/workPermits/WorkPermitView'
import EventBus from '@/utils/eventBus'
import Lifecycle from '@/modules/Lifecycle'
import { aclHelper, tmsPermsTypes, PermTypeMapKeys } from '@/utils/aclHelper'
import { ActionTypes, EmailTypes, lifecycleCommentAttrs, tenancyViewTabs } from '@/utils/consts'
import { getProjectName, getTabIndexByName, showViewTenancyRecordLink } from '@/modules/tenantProjects/utils'
import { getSameBuildingUsers } from '@/api/users/request'
import { sendEmail } from '@/api/postman/request'
import { tenantProjectStatus, tenantProjectTypes } from '@/utils/locale'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import store, { useStoreGetters } from '@/store'
import i18n from '@/utils/i18n'
import { TenantProjectActions, TP_LIFECYCLE_TYPE } from './consts'
import SubmissionsEntry from './SubmissionsEntry'
import ProjectInformation from './information/ProjectInformation'
import ProjectControl from './projectControl/ProjectControl'
import { tenantProjectLeaveMethods } from './leaveTab.methods'
import ContractorInfo from './contractors/ContractorInfo'
import { useTenantProjectSubmissionPins } from './hook'
import { getSubmissionsTabLabelParts } from './submissionsTabLabel'
import { normalizeTenantProjectTabIndex } from './tabSelection'

/** Engineer `/` + `/home` and tenant `/dashboard`: open project without View All list — show View Tenancy Record only (no Back / prev-next). */
const TP_DASHBOARD_ENTRY_ROUTE_NAMES = new Set([
  'engineerDashboard',
  'home',
  'tenantDashboard',
])

/** Dashboard or returning from tenancy detail (browser Back) — same “tenancy-only” header when no global list. */
const TP_TENANCY_ONLY_ENTRY_ROUTE_NAMES = new Set([
  ...TP_DASHBOARD_ENTRY_ROUTE_NAMES,
  tenancyRecordsRouteNames.ccdTenancyView,
  tenancyRecordsRouteNames.ccdTenancyTenantView,
])

function isTenantProjectDashboardLikeEntry (fromRoute) {
  const n = fromRoute && fromRoute.name
  return n != null && n !== '' && TP_DASHBOARD_ENTRY_ROUTE_NAMES.has(String(n))
}

export default {
  components: {
    ContractorInfo,
    DialogSlot,
    HKLHeader,
    SubmissionsEntry,
    ProjectControl,
    ProjectInformation,
    TabsContainer,
    TMSHeader,
    WorkPermitView,
    TextField,
    Lifecycle,
  },
  provide () {
    return {
      'tabNames': this.tabNames,
    }
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const { currentTenantProject, canTenantUpdate } = useStoreGetters('tenantProject', ['currentTenantProject', 'canTenantUpdate'])
    const { canAccessAllProjects } = useStoreGetters('ccd', ['canAccessAllProjects'])
    const { isTenant } = useStoreGetters('user', ['isHKlandUser', 'isTenant'])
    const { showSubmissionsCommentsPin } = useTenantProjectSubmissionPins()

    const status = computed(() => {
      return lodash.get(unref(currentTenantProject), 'status', '')
    })
    const isNotReadonly = computed(() => {
      return status.value !== tenantProjectStatus.CANCELLED && status.value !== tenantProjectStatus.WITHDRAWN
    })
    const canEngineerEdit = computed(() => {
      return aclHelper.getAccess(tmsPermsTypes.project.commentDesign) ||
        aclHelper.getAccess(tmsPermsTypes.project.commentTech) ||
        aclHelper.getAccess(tmsPermsTypes.project.send) ||
        aclHelper.getAccess(tmsPermsTypes.project.createUpdate)
    })
    const canEdit = computed(() => {
      const canTenantEdit = unref(isTenant) && (unref(canAccessAllProjects) || unref(canTenantUpdate))
      return (canEngineerEdit.value || canTenantEdit) && isNotReadonly.value
    })
    const isFitout = computed(() => {
      return lodash.get(unref(currentTenantProject), 'type', '') === tenantProjectTypes.Fitout
    })
    const isReinstatement = computed(() => {
      return lodash.get(unref(currentTenantProject), 'type', '') === tenantProjectTypes.Reinstatement
    })

    const afterSubmitted = computed(() => {
      return (status.value === tenantProjectStatus.SUBMITTED || status.value === tenantProjectStatus.IN_PROGRESS)
    })
    const canCancel = computed(() => {
      return status.value === tenantProjectStatus.DRAFT && canEdit.value
    })
    const canComplete = computed(() => {
      return afterSubmitted.value && canEngineerEdit.value
    })
    // #region withdraw permission
    const hasSubmittedFitOutDocs = computed(() => {
      const docs = lodash.get(unref(currentTenantProject), 'projectDocs', [])
      return docs.some(doc => {
        return lodash.get(doc, 'metaData.submitted') || false
      })
    })
    const canWithdraw = computed(() => {
      if (isReinstatement.value && canEngineerEdit.value && afterSubmitted.value) {
        return true
      }
      if (isFitout.value && afterSubmitted.value) {
        const allowAnyRoleWithdraw = !hasSubmittedFitOutDocs.value && canEdit.value
        const allowEngineerWithdraw = hasSubmittedFitOutDocs.value && canEngineerEdit.value
        return allowAnyRoleWithdraw || allowEngineerWithdraw
      }
      return false
    })
    // #endregion withdraw permission
    const dotOption = computed(() => {
      const withdrawButton = {
        title: i18n.t('action.withdraw'),
        operate: proxy.openWithdrawnModal,
      }
      const cancelButton = {
        title: i18n.t('action.cancel'),
        operate: proxy.openCancelModal,
      }
      const completeButton = {
        title: i18n.t('action.complete'),
        operate: proxy.openCompleteModal,
      }
      const buttons = []
      if (canCancel.value) {
        buttons.push(cancelButton)
      }
      if (canWithdraw.value) {
        buttons.push(withdrawButton)
      }
      if (canComplete.value) {
        buttons.push(completeButton)
      }
      return buttons
    })
    const histories = computed(() => {
      const histories = lodash.get(currentTenantProject.value, 'histories', [])
      const formattedHistories = histories.map(history => {
        const type = lodash.get(history, 'detail.type')
        const isReinstatementLetterSent = type === TP_LIFECYCLE_TYPE.REINSTATEMENT_LETTER_SENT
        const sendBy = lodash.get(history, 'detail.sendBy', {})
        const detailComments = lodash.get(history, 'detail.comments', '')
        const comments = isReinstatementLetterSent ? i18n.t('tenantProjects.lifecycleReinstatementComments', [
          sendBy.displayName,
        ]) : detailComments
        return {
          ...history,
          detail: {
            comments,
          },
        }
      })
      return formattedHistories
    })
    return {
      histories,
      status,
      afterSubmitted,
      canWithdraw,
      isNotReadonly,
      canEngineerEdit,
      canEdit,
      canCancel,
      canComplete,
      dotOption,
      isFitout,
      showSubmissionsCommentsPin,
    }
  },
  data () {
    return {
      tabsHeaderBtns: [],
      activeIndex: 0,
      pendingTabIndex: null,
      projectActionFunc: undefined,
      projectActionHint: '',
      withdrawComment: '',
      addComment: false,
      lifecycleCommentAttrs,
      emptyHistoryHint: this.$t('hint.emptyTenantProjectLifecycle'),
      /** Predecessor route `name` (beforeRouteEnter / beforeRouteUpdate) for dashboard / tenancy-return header. */
      tenantProjectHeaderEntryFrom: '',
    }
  },
  beforeRouteEnter (to, from, next) {
    const fromName = from && from.name != null ? String(from.name) : ''
    if (isTenantProjectDashboardLikeEntry(from)) {
      store.dispatch('listNavigation/clearListNavigationSequence')
    }
    const projectId = lodash.get(to, 'params.id')
    const preload = projectId
      ? store.dispatch('tenantProject/loadTenantProject', projectId)
      : Promise.resolve()
    preload.catch(() => null).then(() => {
      next((vm) => {
        vm.tenantProjectHeaderEntryFrom = fromName
      })
    })
  },
  beforeRouteUpdate (to, from, next) {
    if (String(to.params.id) !== String(from.params.id)) {
      this.tenantProjectHeaderEntryFrom = from && from.name != null ? String(from.name) : ''
    }
    next()
  },
  beforeRouteLeave (to, from, next) {
    const tenancyViewRoutes = [
      tenancyRecordsRouteNames.ccdTenancyView,
      tenancyRecordsRouteNames.ccdTenancyTenantView,
    ]
    if (!tenancyViewRoutes.includes(to.name)) {
      this.clearTenancyBackPath()
    }
    next()
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy', 'canAccessAllProjects']),
    ...mapGetters('listNavigation', ['listNavigationSequence', 'suppressSecondaryBackToSearchResults']),
    ...mapGetters('tenantProject', ['currentTenantProject', 'canTenantUpdate', 'canTenantRead']),
    ...mapGetters('user', ['isTenant', 'currentUser']),
    ...mapGetters('projectContractors', ['isMainUser', 'isSubUser']),
    ...mapGetters('workPermit', ['currentReviewWorkPermit', 'currentWorkPermit', 'reviewQueue', 'isUnderBatch', 'workPermitsInProject']),
    ...mapGetters('regions', ['enableWorkPermitsFeature']),
    ...mapGetters('form', ['dirtyComponents']),
    workPermitListNavRecordIndex () {
      if (!this.hasWorkPermitListNavigation) return -1
      const q = this.$route.query || {}
      const ids = [q.hoistingId, q.wpId].filter(id => id != null && id !== '').map(String)
      return ids.reduce((foundIndex, id) => {
        if (foundIndex >= 0) return foundIndex
        return (this.listNavigationSequence || []).findIndex(e => String(e.id) === id)
      }, -1)
    },
    canGoToPreviousWorkPermitRecord () {
      return this.isUnderBatch && this.workPermitListNavRecordIndex > 0
    },
    canGoToNextWorkPermitRecord () {
      if (!this.isUnderBatch) return false
      const index = this.workPermitListNavRecordIndex
      const seq = this.listNavigationSequence || []
      if (index >= 0) return index < seq.length - 1
      if ((this.reviewQueue || []).length > 0) return true
      return false
    },
    previousWorkPermitListNavEntry () {
      const index = this.workPermitListNavRecordIndex
      if (index <= 0) return null
      return this.listNavigationSequence[index - 1]
    },
    nextWorkPermitListNavEntry () {
      const index = this.workPermitListNavRecordIndex
      const seq = this.listNavigationSequence || []
      if (index < 0 || index >= seq.length - 1) return null
      return seq[index + 1]
    },
    activeBtns () {
      return (this.isNotReadonly && this.tabsHeaderBtns[this.activeIndex]) || []
    },
    tabNames () {
      return [
        { enabled: true, name: this.$t('tenantProjects.projectInformation') },
        { enabled: true, name: this.$t('tenantProjects.submissions') },
        { enabled: this.enableWorkPermitsFeature, name: this.$t('tenantProjects.contractorInfo') },
        { enabled: this.enableWorkPermitsFeature, name: this.$t('workPermit.title') },
        { enabled: true, name: this.$t('tenantProjects.projectControl') },
      ].filter(tab => tab.enabled).map(item => item.name)
    },
    projectControlTabIndex () {
      return this.tabNames.indexOf(this.$t('tenantProjects.projectControl'))
    },
    contractorInfoTabIndex () {
      if (!this.enableWorkPermitsFeature) return -1
      return this.tabNames.indexOf(this.$t('tenantProjects.contractorInfo'))
    },
    submissionsTabLabelParts () {
      return getSubmissionsTabLabelParts(
        this.$t('tenantProjects.submissions'),
        this.$i18n.locale,
      )
    },
    tenancyInfoTitle () {
      return this.currentTenantProject.projectName ||
        getProjectName(this.currentTenantProject, this.currentTenantProject.tenancy || this.currentTenancy)
    },
    canAccessProjects () {
      return this.canAccessAllProjects
    },
    accounts () {
      return (this.currentTenancy && this.currentTenancy.accounts) || []
    },
    canAccessTncAsTenantUser () {
      return this.status !== tenantProjectStatus.WITHDRAWN && this.canAccessProjects && !deep(this.currentTenantProject, 'tncAcceptance.tenantUser')
    },
    canAccessTncAsMainContractor () {
      return this.status !== tenantProjectStatus.WITHDRAWN && this.isMainUser && !deep(this.currentTenantProject, 'tncAcceptance.mainContractor')
    },
    tenancyId () {
      return lodash.get(this.currentTenantProject, 'tenancy.id', '')
    },
    hotbNavBasePath () {
      return this.isTenant ? '/tenants/handover-takeback' : '/handover-takeback'
    },
    tenantProjectNavBasePath () {
      return this.isTenant ? '/tenants/tenant-projects' : '/tenant-projects'
    },
    /**
     * Prev/Next + “View Tenancy Record” HKLHeader: only when list nav was committed from the global
     * `/tenant-projects` list. TenancyView → “All Projects” tab sets `suppressSecondaryBackToSearchResults`
     * so we show the simple “&lt; Back” affordance instead (same store flag as secondary-back suppression).
     */
    hasListNavigationSequence () {
      if (this.suppressSecondaryBackToSearchResults) return false
      const seq = this.listNavigationSequence || []
      if (!seq.length) return false
      if (this.hasWorkPermitListNavigation) return true
      const cur = String(this.$route.params.id)
      return seq.some(e => String(e.id) === cur)
    },
    /**
     * Dashboard → project, or browser Back from tenancy detail: no View All list — show only “View Tenancy Record”
     * (no prev/next). `suppressSecondaryBackToSearchResults` keeps TenancyView “All Projects” embed distinct.
     */
    showTenantProjectTenancyOnlyHeader () {
      if (this.suppressSecondaryBackToSearchResults) return false
      if (this.hasListNavigationSequence) return false
      const pid = String(this.$route.params.id || '')
      if (!pid) return false
      const fromName = this.tenantProjectHeaderEntryFrom
      if (!fromName || !TP_TENANCY_ONLY_ENTRY_ROUTE_NAMES.has(fromName)) return false
      return !!(this.tenancyId || this.$route.params.id)
    },
    tenantProjectHeaderNavigationEntries () {
      if (this.hasListNavigationSequence) return this.listNavigationSequence || []
      return []
    },
    hasWorkPermitListNavigation () {
      const seq = this.listNavigationSequence || []
      if (!seq.length) return false
      const allWorkPermit = seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.WORK_PERMIT)
      if (!allWorkPermit) return false
      if (this.isUnderBatch) return true
      const cur = this.workPermitHeaderCurrentRecordId
      return !!(cur && seq.some(e => String(e.id) === cur))
    },
    workPermitHeaderCurrentRecordId () {
      const q = this.$route.query || {}
      return String(q.hoistingId || q.wpId || '')
    },
    tenantProjectHeaderCurrentRecordId () {
      return this.hasWorkPermitListNavigation ? this.workPermitHeaderCurrentRecordId : this.$route.params.id
    },
    canShowViewTenancyRecordLink () {
      return showViewTenancyRecordLink(this.isMainUser, this.isSubUser)
    },
    tenantProjectHeaderLeftPrimaryLabel () {
      if (this.hasWorkPermitListNavigation) {
        return this.$t('listNavigation.backToDashboard')
      }
      return this.canShowViewTenancyRecordLink
        ? this.$t('handover.backToTenancy')
        : this.$t('back')
    },
    tenantProjectHeaderLeftSecondaryLabel () {
      if (!this.hasListNavigationSequence) return ''
      if (this.hasWorkPermitListNavigation || this.suppressSecondaryBackToSearchResults) return ''
      return this.$t('listNavigation.backToSearchResults')
    },
    /** For HKLHeader prev/next (`pushListNavigationEntry`). */
    hklListNavMergedPaths () {
      return mergeListNavigationHeaderPaths({}, this.hotbNavBasePath, this.tenantProjectNavBasePath)
    },
  },
  watch: {
    '$route.params.id': async function (newId, oldId) {
      if (newId && oldId && (newId !== oldId)) {
        fusionInputCheckUtil.resetAllFields(this.$el)
        await this.loadTenantProject(newId)
        const tenancy = this.currentTenantProject.tenancy
        if (tenancy) {
          this.setCurrentTenancy({tenancy})
        }
        await this.$store.dispatch('listNavigation/suspendTenancyRecordSequenceIfPresent', {
          tenancyId: this.tenancyId,
        })
        this.syncListNavigationSequenceWithRoute()
      }
    },
  },
  async mounted () {
    const projectId = this.$route.params.id
    const loadedProjectId = lodash.get(this.currentTenantProject, 'id', '')
    if (String(loadedProjectId) !== String(projectId)) {
      await this.loadTenantProject(projectId)
    }
    const tenancy = this.currentTenantProject.tenancy
    if (tenancy) {
      this.setCurrentTenancy({tenancy})
    }
    const isFitout = lodash.get(this.currentTenantProject, 'type', '') === tenantProjectTypes.Fitout

    // Only after load tenant project, can get tenant read correctly.
    if (isFitout && (!this.isTenant || this.canTenantRead)) {
      await this.loadProjectPostFitoutSubmissions(projectId)
    }
    let tabIndex = 0
    // TODO should replace index by tab name
    const query = lodash.get(this.$router, 'currentRoute.query', {})
    if (query.tabName) {
      tabIndex = getTabIndexByName(query.tabName)
    }
    if (query.tabIndex) {
      tabIndex = query.tabIndex
    }
    if (this.$refs['tab-container'] && tabIndex) {
      EventBus.$emit('common-tabs-container-select-tab', `#tab-${tabIndex}`, tabIndex)
      this.activeIndex = normalizeTenantProjectTabIndex(tabIndex)
    }
    if (this.canAccessTncAsTenantUser || this.canAccessTncAsMainContractor) {
      this.$refs.projectInformation.loadPageOpenTncModal()
    }
    await this.$store.dispatch('listNavigation/suspendTenancyRecordSequenceIfPresent', {
      tenancyId: this.tenancyId,
    })
    this.syncListNavigationSequenceWithRoute()
  },
  destroyed () {
    this.resetWorkPermit({})
    this.resetMainContracotr()
    this.resetTenantProject()
  },
  methods: {
    ...mapActions('listNavigation', [
      'clearListNavigationSequence',
      'clearTenancyBackPath',
      'setTenancyBackPath',
    ]),
    ...mapActions('tenantProject', ['loadTenantProject', 'updateTenantProjectStatus', 'isUpdateable']),
    ...mapActions('ccd', ['loadTenancy']),
    ...mapActions('workPermit', ['getWorkPermitsInProjectByProjectId']),
    ...mapActions('postFitoutSubmission', ['loadProjectPostFitoutSubmissions']),
    ...mapMutations('ccd', { setCurrentTenancy: mutationTypes.TENANCY_LOAD_CURRENT }),
    ...mapMutations('workPermit', {
      resetWorkPermit: mutationTypes.WORKPERMIT_SET_CURRENT_WORKPERMIT,
    }),
    ...mapMutations('projectContractors', {
      resetMainContracotr: mutationTypes.RESET_STATE }),
    ...mapMutations('tenantProject', {
      resetTenantProject: mutationTypes.TENANT_PROJECT_RESET_PROJECT_SUBMISSION }),
    getActiveIndex (index) {
      const activeIndex = normalizeTenantProjectTabIndex(index)
      this.activeIndex = activeIndex
      if (activeIndex !== 1) EventBus.emit('window_scroll')
    },
    ...tenantProjectLeaveMethods,
    setHeaderBtn (btns = [], tabIndex) {
      this.tabsHeaderBtns.splice(tabIndex, 1, btns)
    },
    openWithdrawnModal () {
      this.addComment = true
      this.projectActionHint = this.$t('hint.tenantWithdrawHint')
      this.projectActionFunc = this.doWithdrawProject
      this.$refs['proj-ops-modal'].openModal()
    },
    openCancelModal () {
      this.projectActionHint = this.$t('hint.tennatProjectAction', [this.$t(`action.${TenantProjectActions.cancel}`)])
      this.projectActionFunc = this.doCancelProject
      this.$refs['proj-ops-modal'].openModal()
    },
    openCompleteModal () {
      this.projectActionHint = this.$t('hint.tennatProjectAction', [this.$t(`action.${TenantProjectActions.complete}`)])
      this.projectActionFunc = this.doCompleteProject
      this.$refs['proj-ops-modal'].openModal()
    },
    closeModal () {
      this.projectActionHint = ''
      this.projectActionFunc = undefined
      this.$refs['proj-ops-modal'].closeModal()
    },
    beforeCloseModal () {
      this.addComment = false
    },
    async exeOperate () {
      if (this.addComment && !this.validateFields()) { return }
      const isUpdateable = await this.isUpdateable(this.$router.currentRoute.params.id)
      if (isUpdateable && this.projectActionFunc) {
        await this.projectActionFunc()
      }
      this.loadTenantProject(this.currentTenantProject.id)
      this.closeModal()
    },
    async doCancelProject () {
      await this.updateTenantProjectStatus({
        request: { status: tenantProjectStatus.CANCELLED },
        action: ActionTypes.tenantProjects.cancel,
      })
    },
    validateFields () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.font-bold')
      return fusionInputCheckUtil.isAllFieldsValid('.font-bold')
    },
    async doWithdrawProject () {
      await this.updateTenantProjectStatus({
        request: { status: tenantProjectStatus.WITHDRAWN, comments: this.withdrawComment },
        action: ActionTypes.tenantProjects.withdraw,
      })
      await this.sendmailForWithdraw()
    },
    async doCompleteProject () {
      await this.updateTenantProjectStatus({
        request: { status: tenantProjectStatus.COMPLETED },
        action: ActionTypes.tenantProjects.complete,
      })
      if (this.enableWorkPermitsFeature) {
        await this.getWorkPermitsInProjectByProjectId({projectId: this.currentTenantProject.id, changeCurrentWorkPermitId: this.currentWorkPermit.id})
      }
    },
    async sendmailForWithdraw () {
      let tenantAccounts = this.getTenantRepresentative()
      tenantAccounts.push(this.getCreator())
      const hkldUsers = await this.getUsersOfSameBuilding()
      tenantAccounts = tenantAccounts.concat(hkldUsers)
      const extMetaData = {
        to: tenantAccounts,
        title: this.tenancyInfoTitle,
        tradeName: lodash.get(this.currentTenantProject, 'tenancy.tradeName', ''),
        comments: this.withdrawComment,
      }
      await sendEmail(EmailTypes.tenantProjects.withdrawTenantProject, extMetaData, false)
    },
    getCreator () {
      const username = lodash.get(this.currentTenantProject, 'creator.username', '')
      const accounts = lodash.get(this.currentTenantProject, 'creator.contacts', [])
      let email = ''
      accounts.map(account => {
        if (account.description === 'Email') {
          email = account.value
        }
      })
      return email || username
    },
    getTenantRepresentative () {
      const emails = []
      const accounts = lodash.get(this.currentTenantProject, 'accounts', [])
      accounts.map(account =>
        emails.push(account.username),
      )
      return emails
    },

    async getUsersOfSameBuilding () {
      const tenancy = this.currentTenantProject.tenancy
      const users = await getSameBuildingUsers(tenancy.building.id, tenancy.type, false, PermTypeMapKeys.TENANT_PROJECT_UPDATE)
      return users.map(user => user.email)
    },
    lifecycleModal () {
      this.$refs.tenantProjectLifecycle.init()
    },
    onHklListNavEntry (entry) {
      pushListNavigationEntry(this.$router, this.$route, this.hklListNavMergedPaths, entry)
    },
    onTenantProjectHklPrevious (entry) {
      if (this.isUnderBatch) {
        this.toPreviousWorkPermit()
        return
      }
      if (entry) this.onHklListNavEntry(entry)
    },
    onTenantProjectHklNext (entry) {
      if (this.isUnderBatch) {
        this.toNextWorkPermit()
        return
      }
      if (entry) this.onHklListNavEntry(entry)
    },
    onTenantProjectHklLeftPrimary () {
      if (this.hasWorkPermitListNavigation) {
        const go = () => navigation.toPage('workPermitDashboard')
        if (this.hasOtherTabUnsavedEdits()) {
          this.promptLeavePageWithOtherTabEdits(go)
          return
        }
        go()
      } else if (this.canShowViewTenancyRecordLink) {
        this.navToTenancy()
      } else if (this.hasOtherTabUnsavedEdits()) {
        this.promptLeavePageWithOtherTabEdits(() => this.$router.goBack())
      } else {
        this.$router.goBack()
      }
    },
    doNavToTenancy () {
      navigateToTenancyFromTenantProject(navigation, {
        isTenant: this.isTenant,
        tenancyId: this.tenancyId,
        backPath: this.$route.fullPath,
        setBackPath: this.setTenancyBackPath,
      })
    },
    navToTenancy () {
      // Four tabs with edits: Cancel/Continue before View Tenancy Record / leaving page
      if (this.hasOtherTabUnsavedEdits()) {
        this.promptLeavePageWithOtherTabEdits(() => this.doNavToTenancy())
        return
      }
      this.doNavToTenancy()
    },
    navToAllTenantProjects () {
      const go = () => {
        if (this.isTenant) {
          const id = this.tenancyId
          if (id) {
            navigation.toPage('ccdTenancyTenantView', { id }, { tabIndex: tenancyViewTabs.allProjects })
          }
        } else {
          navigation.toPage('allTenantProjects', {}, { [VIEW_ALL_RESUME_SEARCH_QUERY]: '1' })
        }
      }
      if (this.hasOtherTabUnsavedEdits()) {
        this.promptLeavePageWithOtherTabEdits(go)
        return
      }
      go()
    },
    syncListNavigationSequenceWithRoute () {
      const seq = this.listNavigationSequence || []
      if (!seq.length) return

      const allWorkPermit = seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.WORK_PERMIT)
      if (allWorkPermit) {
        const cur = String(this.workPermitHeaderCurrentRecordId)
        if (!cur || !seq.some(e => String(e.id) === cur)) {
          if (!this.isUnderBatch) {
            this.clearListNavigationSequence()
          }
        }
        return
      }

      const projectId = String(this.$route.params.id || '')
      if (!projectId || !seq.some(e => String(e.id) === projectId)) {
        this.clearListNavigationSequence()
      }
    },
    async toPreviousWorkPermit () {
      if (!this.canGoToPreviousWorkPermitRecord) return
      const workPermitView = this.$refs['work-permit-view']
      if (this.previousWorkPermitListNavEntry) {
        pushListNavigationEntry(this.$router, this.$route, this.hklListNavMergedPaths, this.previousWorkPermitListNavEntry)
        return
      }
      if (workPermitView) await workPermitView.toPreviousWorkPermit()
    },
    async toNextWorkPermit () {
      if (!this.canGoToNextWorkPermitRecord) return
      const workPermitView = this.$refs['work-permit-view']
      if (this.nextWorkPermitListNavEntry) {
        pushListNavigationEntry(this.$router, this.$route, this.hklListNavMergedPaths, this.nextWorkPermitListNavEntry)
        return
      }
      if (workPermitView && await workPermitView.toNextWorkPermit()) return
      if (workPermitView) await workPermitView.advanceReviewQueueToNextWorkPermit()
    },
  },
}
</script>

<style lang="scss">
.withDraw {
  margin-top: 15px
}
.tenant-projects {
  height: 100%;
  background: #EBEBEB;
  .tms-header {
    padding-left: 15px;
    padding-right: 15px;
    .main-title {
      font-weight: normal;
    }
  }
  &__tab-pin.pin__bubble-orange {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 17px;
    margin-left: 6px;
    vertical-align: middle;
    i {
      display: block;
      position: relative;
      top: auto;
      left: auto;
      line-height: 1;
    }
  }
  &__submissions-tab-label {
    display: inline-grid;
    grid-template-columns: auto auto;
    grid-template-rows: 20px 20px;
    column-gap: 3px;
    align-items: center;
    justify-content: center;
    line-height: 20px;
  }
  &__submissions-tab-label--single-line {
    display: inline-flex;
    gap: 4px;
    white-space: nowrap;

    .tenant-projects__submissions-tab-line,
    .tenant-projects__submissions-tab-pin-cell {
      grid-column: auto;
      grid-row: auto;
    }
  }
  &__submissions-tab-line {
    white-space: nowrap;
  }
  &__submissions-tab-line--primary {
    grid-column: 1;
    grid-row: 1;
  }
  &__submissions-tab-line--secondary {
    grid-column: 1;
    grid-row: 2;
  }
  &__submissions-tab-pin-cell {
    display: inline-flex;
    grid-column: 2;
    grid-row: 1 / span 2;
    align-items: center;
    justify-content: center;

    .tenant-projects__tab-pin {
      width: 24px;
      height: 14px;
      margin-left: 0;
      flex-shrink: 0;
    }
  }
  #sticky-tabs {
    .tenant-projects__submissions-tab-label {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      line-height: 20px;
      white-space: nowrap;
    }
    .tenant-projects__submissions-tab-line--primary,
    .tenant-projects__submissions-tab-line--secondary,
    .tenant-projects__submissions-tab-pin-cell {
      grid-column: auto;
      grid-row: auto;
    }
    .tenant-projects__submissions-tab-line--primary {
      order: 1;
    }
    .tenant-projects__submissions-tab-line--secondary {
      order: 2;
    }
    .tenant-projects__submissions-tab-pin-cell {
      order: 3;

      .tenant-projects__tab-pin {
        margin-left: 2px;
      }
    }
  }
  .tms-header .content,
  .tenant-projects-tabContainer .create-project-main{
    max-width: 1440px;
    margin: 0 auto;
    #common-tabs .tabs-title {
      flex: 1 1 0;
    }
  }
  .tenant-projects-tabContainer {
    padding: 15px;
    background: #EBEBEB;
    & > div {
      height: 100%;
    }

  }
  .tenant-project-status{
    cursor: pointer;
  }
  .create-project-main {
    height: 100%;
    #common-tabs{
      margin-bottom: 2px;
      li.tabs-title {
        box-shadow: 0 0 4px 0 rgba(234, 234, 234, 0.5);
        a {
          background: white;
        }
      }
    }
    .hkld-tabs-content .tabs-panel {
      background: white;
      box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
      padding: 20px;
    }
  }
  .tenant-representative,
  .basic-information {
    background: white;
  }
  .tenant-representative {
    padding-left: 16px;
    .display-table {
      margin-left: 0;
    }
  }
}
</style>
